import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { cva } from 'class-variance-authority';
import { createPortal } from 'react-dom';
import { useNavigateToEntity } from '@/hooks/navigation';
import { Flex, Text, Button, Dropdown, IconButton, Tooltip, message, notification, space, iconWeight, Icon, tableActionsCell, Avatar, Pagination } from '@nula/core-ui';
import { ShareDialog } from '@/lib/sharing/components/share-dialog';
import { useCanEdit } from '@/lib/sharing/hooks/use-can-edit';
import { useAuthStore } from '@/stores/use-auth-store';
import { Library } from '@/components/document-cards/library';
import { DocumentCard } from '@/components/document-cards/document-card';
import { formatDate } from '@/utils/format-date';
import { shortcutLabel } from '@/lib/platform/platform';
import { CollectionTableNameCell } from './collection-table-name-cell';
import { kindIcon } from '@/lib/documents/doc-title';
import { useWorkspaceDocs } from '@/lib/sync/hooks/use-workspace-docs';
import { useWorkspaceCollections } from '@/lib/sync/hooks/use-workspace-collections';
import { useWorkspaceService } from '@/lib/workspace/use-workspace-service';
import { SelectionActions } from '@/components/document-cards/selection-actions';
import { getEntityKindMeta } from '@/utils/entity-kind-meta';
import styles from './collection-detail.module.css';
import tableStyles from '@/components/document-cards/library-table.module.css';

const collectionDetail = cva(styles.actionsWrap);
const createBtn = cva(styles.createBtn);

// ── Add Existing Document Popover ───────────────────────────────────────────

function AddDocumentPopover({
  collectionId: _collectionId,
  existingDocIds,
  onAdd,
  onClose,
  anchorRef,
}: {
  collectionId: string;
  existingDocIds: Set<string>;
  onAdd: (entityId: string) => void;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLDivElement | null>;
}) {
  const allDocs = useWorkspaceDocs();
  const allDocuments = useMemo(() => allDocs.filter((d) => !d.isArchived), [allDocs]);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  const available = allDocuments
    .filter((d) => !existingDocIds.has(d.id))
    .filter((d) => !search.trim() || (d.title || '').toLowerCase().includes(search.toLowerCase()));

  const [pos, setPos] = useState({ top: 0, left: 0 });
  useEffect(() => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    setPos({ top: rect.bottom + 8, left: rect.left });
  }, [anchorRef]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  return createPortal(
    <Flex
      ref={ref}
      vertical
      className={styles.popover}
      style={{ '--popover-top': `${pos.top}px`, '--popover-left': `${pos.left}px` } as React.CSSProperties}
    >
      <Flex
        align="center"
        gap="xs"
        className={styles.searchRow}
      >
        <Icon name="search" size={14} weight={iconWeight.light} className={styles.searchIcon} />
        <input
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search documents..."
          className={styles.searchInput}
        />
      </Flex>
      <Flex vertical className={styles.docList}>
        {available.length === 0 ? (
          <Text type="secondary" size="sm" className={styles.emptyText}>
            {allDocuments.length === existingDocIds.size ? 'All documents already added' : 'No matches'}
          </Text>
        ) : available.map((doc) => (
          <DocPickerRow
            key={doc.id}
            title={doc.title || 'Untitled'}
            type={doc.kind}
            onClick={() => { onAdd(doc.id); }}
          />
        ))}
      </Flex>
    </Flex>,
    document.body,
  );
}

function DocPickerRow({ title, type, onClick }: { title: string; type: string; onClick: () => void }) {
  return (
    <Flex
      align="center"
      gap="sm"
      role="option"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      className={styles.docPickerRow}
    >
      <Icon name={kindIcon(type)} size={14} weight={iconWeight.light} className={styles.docPickerIcon} />
      <Text size="sm" ellipsis>{title}</Text>
    </Flex>
  );
}

// ── Main component ──────────────────────────────────────────────────────────

interface CollectionDetailProps {
  collectionId: string;
  isBackground?: boolean;
}

export function CollectionDetail({ collectionId, isBackground: _isBackground }: CollectionDetailProps) {
  const user = useAuthStore((s) => s.user);
  const canEdit = useCanEdit(collectionId);
  const service = useWorkspaceService();
  const allDocs = useWorkspaceDocs();

  const navigateToEntity = useNavigateToEntity();

  const openDocument = useCallback((id: string) => {
    navigateToEntity(id);
  }, [navigateToEntity]);

  const actionsRef = useRef<HTMLDivElement>(null);

  // Derive collection name from the workspace projection
  const allCollections = useWorkspaceCollections();
  const collectionEntry = collectionId
    ? allCollections.find((c) => c.id === collectionId)
    : undefined;
  const collectionName = collectionEntry?.name ?? 'Collection';

  // Compute filtered documents from projection
  const filteredDocuments = useMemo(
    () => collectionId
      ? allDocs.filter((d) => !d.isArchived && d.collectionIds.includes(collectionId))
      : [],
    [allDocs, collectionId],
  );

  const PAGE_SIZE = 12;
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState('modified');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);

  const docTags = useMemo(() => {
    const tagSet = new Set<string>();
    filteredDocuments.forEach((d) => d.tags?.forEach((t) => tagSet.add(t)));
    return Array.from(tagSet).sort();
  }, [filteredDocuments]);

  const documents = useMemo(() => {
    let docs = filteredDocuments;
    if (searchQuery.trim()) {
      docs = docs.filter((d) => (d.title || '').toLowerCase().includes(searchQuery.toLowerCase()));
    }
    if (selectedTags.length > 0) {
      docs = docs.filter((d) => d.tags?.some((t) => selectedTags.includes(t)));
    }
    if (sortBy === 'alpha') {
      docs = [...docs].sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    } else {
      docs = [...docs].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    }
    return docs;
  }, [filteredDocuments, searchQuery, selectedTags, sortBy]);

  const existingDocIds = new Set(documents.map((d) => d.id));

  const totalPages = Math.max(1, Math.ceil(documents.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedItems = documents.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleArchive = useCallback((id: string) => {
    service.archiveDoc(id, true);
  }, [service]);

  const handleRemoveFromCollection = useCallback((entityId: string) => {
    if (!collectionId) return;
    service.removeFromCollection(entityId, collectionId);
    message.success({ content: 'Removed from collection', key: 'collection-remove' });
  }, [collectionId, service]);

  const tableColumns = useMemo(() => [
    {
      title: 'Title',
      key: 'name',
      sorter: (a: typeof documents[number], b: typeof documents[number]) => (a.title || '').localeCompare(b.title || ''),
      render: (_: unknown, doc: typeof documents[number]) => <CollectionTableNameCell doc={doc} />,
    },
    {
      title: 'Owner',
      key: 'owner',
      width: 140,
      render: () => (
        <Flex align="center" gap={space[2]}>
          {user?.picture ? (
            <Avatar src={user.picture} size="sm" />
          ) : (
            <Flex align="center" justify="center" className={tableStyles.avatarPlaceholder}>
              <Icon name="person" size={12} weight={iconWeight.light} />
            </Flex>
          )}
          {user?.name && (
            <Text className={tableStyles.ownerName}>{user.name.split(' ')[0]}</Text>
          )}
        </Flex>
      ),
    },
    {
      title: 'Last Edited',
      key: 'updatedAt',
      width: 140,
      sorter: (a: typeof documents[number], b: typeof documents[number]) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime(),
      render: (_: unknown, doc: typeof documents[number]) => (
        <Text className={tableStyles.dateText}>{formatDate(doc.updatedAt)}</Text>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 48,
      render: (_: unknown, doc: typeof documents[number]) => (
        <Flex className={tableActionsCell} onClick={(e) => e.stopPropagation()}>
          <Dropdown
            trigger={['click']}
            placement="bottom-end"
            menu={{
              items: [
                { key: 'open', label: 'Open', icon: <Icon name="open_in_new" size={14} weight={iconWeight.light} />, onClick: () => openDocument(doc.id) },
                { key: 'd1', label: '', type: 'divider' as const },
                { key: 'remove', label: 'Remove from Collection', icon: <Icon name="folder_off" size={14} weight={iconWeight.light} />, onClick: () => handleRemoveFromCollection(doc.id) },
                { key: 'trash', label: 'Move to Trash', icon: <Icon name="delete" size={14} weight={iconWeight.light} />, danger: true, onClick: () => handleArchive(doc.id) },
              ],
            }}
          >
            <IconButton size="sm" aria-label="More options" className={tableStyles.moreBtn}>
              <Icon name="more_horiz" size={16} weight={iconWeight.light} />
            </IconButton>
          </Dropdown>
        </Flex>
      ),
    },
  // `documents` is referenced only via `typeof` (compile-time type), not at runtime.
   
  ], [openDocument, handleRemoveFromCollection, handleArchive, user]);

  const handleBulkRemoveFromCollection = useCallback(() => {
    if (!collectionId) return;
    selectedRowKeys.forEach((id) => service.removeFromCollection(id, collectionId));
    const count = selectedRowKeys.length;
    setSelectedRowKeys([]);
    notification.success({ message: `Removed ${count} item${count === 1 ? '' : 's'} from collection`, duration: 4000 });
  }, [selectedRowKeys, collectionId, service]);

  const handleBulkArchive = useCallback(() => {
    selectedRowKeys.forEach((id) => {
      service.archiveDoc(id, true);
    });
    const count = selectedRowKeys.length;
    setSelectedRowKeys([]);
    notification.success({ message: `Moved ${count} item${count === 1 ? '' : 's'} to trash`, duration: 4000 });
  }, [selectedRowKeys, service]);

  const handleAddExisting = (entityId: string) => {
    if (!collectionId) return;
    service.addToCollection(entityId, collectionId);
    setPickerOpen(false);
    message.success({ content: 'Added to collection', key: 'collection-add' });
  };

  const handleCreateNew = async (type: 'document' | 'canvas' = 'document') => {
    if (!collectionId) return;
    const { id } = await service.createDoc({ kind: type, collectionId });
    navigateToEntity(id);
  };

  return (
    <>
    <Library
      title={collectionName}
      items={pagedItems}
      tableColumns={tableColumns}
      onTableRow={(doc) => ({ onClick: () => navigateToEntity(doc.id as string) })}
      selectedIds={new Set(selectedRowKeys)}
      onToggleSelect={(id) => setSelectedRowKeys((prev) => prev.includes(id) ? prev.filter((k) => k !== id) : [...prev, id])}
      onSelectAll={(ids) => setSelectedRowKeys(ids)}
      onClearSelection={() => setSelectedRowKeys([])}
      tableRowSelection={{ selectedRowKeys, onChange: setSelectedRowKeys }}
      selectionActions={
        selectedRowKeys.length > 0 ? (
          <SelectionActions
            actions={[
              { key: 'remove', icon: 'folder_off', label: 'Remove from Collection', onClick: handleBulkRemoveFromCollection },
              { key: 'trash', icon: 'delete', label: 'Move to Trash', onClick: handleBulkArchive, danger: true },
            ]}
          />
        ) : undefined
      }
      renderCard={(doc, _index, vm, checkboxNode) => (
        <DocumentCard
          doc={doc}
          listView={vm === 'list'}
          onOpen={(id) => openDocument(id)}
          onArchive={handleArchive}
          checkboxNode={checkboxNode}
          extraMenuItems={collectionId ? [
            {
              key: 'remove-from-collection',
              label: 'Remove from Collection',
              icon: <Icon name="folder_off" size={14} weight={iconWeight.light} />,
              onClick: () => handleRemoveFromCollection(doc.id),
            },
          ] : undefined}
        />
      )}
      keyExtractor={(doc) => doc.id}
      searchPlaceholder="Search in collection..."
      onSearch={(q) => { setSearchQuery(q); setPage(1); }}
      emptyIllustration="documents"
      emptyTitle="This collection is empty"
      emptyMessage="Start adding documents to bring this collection to life."
      cardLayout={viewMode === 'grid' ? 'pageGrid' : undefined}
      heroIcon={getEntityKindMeta('collection').icon}
      heroCount={`${documents.length} Document${documents.length !== 1 ? 's' : ''}`}
      tags={docTags}
      selectedTags={selectedTags}
      onTagsChange={(t) => { setSelectedTags(t); setPage(1); }}
      viewMode={viewMode}
      onViewChange={setViewMode}
      sortOptions={[
        { key: 'modified', label: 'Last Modified', icon: <Icon name="schedule" size={14} weight={iconWeight.light} /> },
        { key: 'alpha', label: 'Alphabetical', icon: <Icon name="sort_by_alpha" size={14} weight={iconWeight.light} /> },
      ]}
      activeSort={sortBy}
      onSortChange={(s) => { setSortBy(s); setPage(1); }}
      onRenameTitle={collectionId && canEdit ? (newName) => service.renameCollection(collectionId, newName) : undefined}
      toolbarExtras={collectionId ? (
        <Tooltip title="Share" placement="bottom">
          <IconButton
            size="lg"
            aria-label="Share collection"
            onClick={() => setShareOpen(true)}
            className={styles.toolbarIconBtn}
          >
            <Icon name="share" size={20} weight={iconWeight.light} />
          </IconButton>
        </Tooltip>
      ) : undefined}
      actions={
        <Flex ref={actionsRef} align="center" gap={space[2]} className={collectionDetail()}>
          <Dropdown
            trigger={['click']}
            placement="bottom-end"
            menu={{
              items: [
                {
                  key: 'new-document',
                  label: 'New Document',
                  icon: <Icon name={getEntityKindMeta('document').icon} size={14} weight={iconWeight.light} />,
                  extra: <Text size="xs" type="secondary">{shortcutLabel('⌘T', 'Ctrl+T')}</Text>,
                  onClick: () => handleCreateNew('document'),
                },
                {
                  key: 'new-canvas',
                  label: 'New Canvas',
                  icon: <Icon name={getEntityKindMeta('canvas').icon} size={14} weight={iconWeight.light} />,
                  extra: <Text size="xs" type="secondary">{shortcutLabel('⌘⇧T', 'Ctrl+Shift+T')}</Text>,
                  onClick: () => handleCreateNew('canvas'),
                },
                { key: 'd1', label: '', type: 'divider' as const },
                {
                  key: 'add-existing',
                  label: 'Add Existing',
                  icon: <Icon name="note_add" size={14} weight={iconWeight.light} />,
                  onClick: () => setPickerOpen(true),
                },
              ],
            }}
          >
            <Button
              variant="primary"
              icon={<Icon name="add" size={18} weight={iconWeight.light} />}
              className={createBtn()}
            >
              New Document
            </Button>
          </Dropdown>
          {pickerOpen && collectionId && (
            <AddDocumentPopover
              collectionId={collectionId}
              existingDocIds={existingDocIds}
              onAdd={handleAddExisting}
              onClose={() => setPickerOpen(false)}
              anchorRef={actionsRef}
            />
          )}
        </Flex>
      }
      pagination={
        documents.length > PAGE_SIZE ? (
          <Flex justify="center" className={styles.paginationRow}>
            <Pagination
              current={currentPage}
              total={documents.length}
              pageSize={PAGE_SIZE}
              onChange={setPage}
            />
          </Flex>
        ) : undefined
      }
    />
    {collectionId && (
      <ShareDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        resourceId={collectionId}
        resourceTitle={collectionName}
        resourceType="collection"
      />
    )}
    </>
  );
}
